import type { Message } from '../../../lib/use-chat';
import { API_BASE } from '../../../lib/api-base';
import { apiFetch } from '../../../lib/api-client';

// Light speech-shaping for the voice recovery path (the server does the richer
// version for the normal path). Strips <voice> mimicry and markdown so a
// recovered reply reads cleanly aloud.
function speechSanitize(text: string): string {
  return text
    .replace(/<\/?voice>/gi, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1 (in your chat)')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/[*_`#]/g, '')
    .replace(/^\s*---\s*$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Recover the assistant's answer when the voice ask stream drops mid-flight
 * (proxy/gateway 502 on a long turn). The harness loop runs independently of
 * our observing connection, so the answer is almost always persisted moments
 * later — poll for the newest assistant message created at/after the request
 * and speak that, instead of a false "snag".
 */
export async function recoverVoiceReply(conversationId: string, sinceMs: number): Promise<string | null> {
  for (let attempt = 0; attempt < 8; attempt++) {
    await new Promise((r) => setTimeout(r, attempt === 0 ? 800 : 2200));
    try {
      const res = await apiFetch(`${API_BASE}/api/conversations/${conversationId}/messages`, {
        credentials: 'include',
      });
      if (!res.ok) continue;
      const data = (await res.json()) as { messages?: Message[] };
      const msgs = data.messages || [];
      for (let i = msgs.length - 1; i >= 0; i--) {
        const m = msgs[i];
        if (m.role !== 'ASSISTANT' && m.role !== 'ASSISTANT_STEP') continue;
        const created = m.createdDate ? Date.parse(m.createdDate) : 0;
        if (created >= sinceMs - 5000 && m.content) {
          const clean = speechSanitize(m.content);
          if (clean) return clean;
        }
        break;
      }
    } catch {
      // transient — try again
    }
  }
  return null;
}
